import { computed, ref } from 'vue'
import { isEmpty } from 'global-utils'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useLogin } from '@/StoreWeb/js/composables/login-utils'
import { checkHasToCreateMspAccount, triggerAddAccountEvent } from '@/StoreWeb/js/composables/msp-account-utils'
import emitter from 'global-emitter'
import * as catalogMutationTypes from '@/StoreWeb/store/modules/catalog/mutation-types'
import * as mainMutationTypes from '@/StoreWeb/store/modules/main/mutation-types'
import * as cartActionTypes from '@/StoreWeb/store/modules/cart/action-types'
import addCartFlowMixins from '@/StoreWeb/js/mixins/add-cart-flow-utils'
import CartSerializer from '@/StoreWeb/serializers/CartSerializerSingleton'

export const useProduct = (props) => {
    const { t } = useI18n()

    const store = useStore()
    const router = useRouter()
    const { isAuthenticated } = useLogin()
    const { storeAddCartFlow, triggerLogin } = addCartFlowMixins()

    const cartSerializer = ref(CartSerializer.getInstance())

    const cart = computed(() => store.state.cartModule.cart)
    const getComponent = computed(() => props.linkToProductPage && !showExpressPurchase.value && !showMoreInfo.value ? 'router-link' : 'div')
    const getHasSelectedFareMedia = computed(() => selectedFareMedia.value?.id)
    const getImage = computed(() => {
        if (!isEmpty(props.product.fareMedia)) {
            return props.product.fareMedia.image
        }

        return props.product.image
    })
    const getImageAlt = computed(() => {
        if (props.product?.imageAlt) {
            return props.product.imageAlt
        }

        return ''
    })
    const getTitleTag = computed(() => {
        if (props.level === 1) {
            return 'h2'
        }

        if (props.level === 2) {
            return 'h3'
        }

        if (props.level === 3) {
            return 'h4'
        }

        return 'h5'
    })
    const getUserStatus = computed(() => store.getters.getUserStatus)
    const hasTargetIcons = computed(() => targetIcons.value.length > 0)
    const isNonSaleable = computed(() => props.product.saleableUnit.id === 'NON_SALEABLE' && props.product.getFare() === 0)
    const targetIcons = computed(() => props.product.getTargets())
    const selectedFareMedia = computed(() => store.state.userModule.selectedFareMedia)
    const userPendingRequests = computed(() => store.state.userModule.userPendingRequests)
    const showAddToCart = computed(() => {
        return props.product.saleableUnit.id !== 'NON_SALEABLE' &&
            (
                getHasSelectedFareMedia.value !== true ||
                (
                    getHasSelectedFareMedia.value === true &&
                    props.product.requiredFareProfiles.length === 0
                )
            )
    })
    const showExpressPurchase = computed(() => {
        return showAddToCart.value && props.product.expressPurchase
    })
    const showMoreInfo = computed(() => getHasSelectedFareMedia.value === true && props.product.requiredFareProfiles.length > 0)

    async function handleAddToCartCommon () {
        if (isAuthenticated.value) return true

        const product = props.product
        if (!product) return true

        const isModal = product.modalType === 'MODALPRODUCT' || product.modalType === 'MODALPRODUCTGROUP'
        const requiresAuthModes = typeof product.getUserAuthModes === 'function' && !isEmpty(product.getUserAuthModes())

        if (!isModal || !requiresAuthModes) return true

        if (typeof product.hasToLoginOnAddToCart === 'function' && product.hasToLoginOnAddToCart()) {
            storeAddCartFlow({ product })
        }

        emitter.emit('showProductModal', product)
        return false
    }

    async function addCartAction () {
        if (isAuthenticated.value) {
            const { hasToCreateMspAccount, provider } = checkHasToCreateMspAccount(props.product)

            if (!userPendingRequests.value.getWallet && getUserStatus.value === 'BLOCKED_PAYMENT_REQUIRED') {
                emitter.emit('showAlertModal', {
                    messageHTML: t('wallet:blocked_payment_required:message'),
                    title: t('wallet:blocked_payment_required:title')
                })

                return
            }

            if (hasToCreateMspAccount) {
                store.commit(catalogMutationTypes.SET_PRODUCT_TO_ADD_ONCE_ACCOUNT_FINALIZED, props.product)
                triggerAddAccountEvent(provider)

                return
            }

            emitter.emit('showProductModal', props.product)

            return
        }

        const continueFlow = await handleAddToCartCommon()
        if (!continueFlow) return

        storeAddCartFlow({
            product: props.product
        })

        triggerLogin({
            callback: emitter.emit('hideProductModal', { keep: true })
        })
    }

    async function expressPurchaseAddToCart () {
        const continueFlow = await handleAddToCartCommon()
        if (!continueFlow) return

        const basketInfo = store.state.userModule.isAuthenticated ? 'PROVIDERACCOUNTANDFAREMEDIA' : 'PROVIDERFAREMEDIA'
        const currentOption = props.product.saleOptions.find(option => option.basketInfo.id === basketInfo)
        const deliveryMode = currentOption ? currentOption.deliveryMode?.id : 'DISTRIBUTION'
        const mainAccountProviderUser = ref(null)

        const isAssociatedAccount = cartSerializer.value.isAssociatedProviderUser(props.product.saleOptions[0].provider.id, selectedFareMedia.value?.providerUserExternalId)

        if (isAssociatedAccount) {
            mainAccountProviderUser.value = cartSerializer.value.getMainAccountProviderUser(props.product.saleOptions[0].provider.id)
        }

        const { firstName, lastName } = getHolderInfo(selectedFareMedia.value)

        const body = {
            orderId: cart.value.id,
            items: [
                {
                    type: props.product.modalType,
                    expressPurchase: true,
                    id: props.product.id,
                    version: 1,
                    saleableUnit: props.product.saleableUnit.unit,
                    quantity: props.product.quantity,
                    saleOption: {
                        target: props.product.saleOptions[0].target.id,
                        deliveryMode,
                        basketInfo,
                        providerId: props.product.saleOptions[0].provider.id,
                        providerUserId: mainAccountProviderUser.value ? mainAccountProviderUser.value.id : selectedFareMedia.value?.providerUserId ?? '',
                        providerUserFirstName: firstName,
                        providerUserLastName: lastName,
                        providerUserExternalId: mainAccountProviderUser.value ? mainAccountProviderUser.value.providerUserExternalId : selectedFareMedia.value?.providerUserExternalId ?? '',
                        fareMediaId: selectedFareMedia.value.id ?? ''
                    },
                    parameters: props.product.preSetProductParameters
                }
            ]
        }

        const { recipientUser } = selectedFareMedia.value
        if (recipientUser?.associationId || recipientUser?.associationType) {
            body.items[0].recipientUser = recipientUser.getAddToCartBody()
        }

        const productToAdd = props.product
        store.commit(mainMutationTypes.SET_SHOW_GLOBAL_LOADER, { value: true })
        await store.dispatch(cartActionTypes.ADD_TO_CART, {
            addToCartBody: body,
            isExpress: true
        })
        store.commit(mainMutationTypes.SET_SHOW_GLOBAL_LOADER, { value: false })
        await router.push({ name: 'cart' })
        productToAdd.parameters = productToAdd.preSetProductParameters
    }

    function showDebugModal (event) {
        emitter.emit('showDebugModal', {
            objectToDump: props.product,
            title: 'Propriétés du produit #' + props.product.id
        })
    }

    function getHolderInfo (fareMedia) {
        if (fareMedia?.account) {
            return {
                firstName: fareMedia.account.firstName ?? '',
                lastName: fareMedia.account.lastName ?? ''
            }
        }

        if (fareMedia?.firstName || fareMedia?.lastName) {
            return {
                firstName: fareMedia.firstName ?? '',
                lastName: fareMedia.lastName ?? ''
            }
        }

        if (fareMedia?.recipientUser) {
            return {
                firstName: fareMedia.recipientUser.firstName ?? '',
                lastName: fareMedia.recipientUser.lastName ?? ''
            }
        }

        return {
            firstName: '',
            lastName: ''
        }
    }

    return {
        getComponent,
        getImage,
        getImageAlt,
        getTitleTag,
        hasTargetIcons,
        isNonSaleable,
        showAddToCart,
        showExpressPurchase,
        showMoreInfo,
        targetIcons,
        addCartAction,
        expressPurchaseAddToCart,
        showDebugModal
    }
}
